import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import strings from 'utils/strings'
import styled from '@emotion/styled'
import { themeColor } from 'utils/styles'
import { LIST, DASH_VIEW } from 'utils/constants'
import { prop, isEmpty } from 'ramda'
import { useDispatch } from 'react-redux'
import { useSelector } from 'hooks/redux'
import {
  setSelectedIdWithDrawerAndMeta,
  setSelectedIdWithDrawer,
} from 'actions/navigator'
import {
  getMovedComponentsList,
  getRemovedComponentsList,
  getAddedComponentsList,
  getSelectedCustomGraphics,
  getRemovedCustomGraphics,
  getUpdatedLocationNames,
  getIsAllium,
} from 'selectors/dash'
import { saveDash } from 'actions/dash'
import { Content, Footer } from 'components/SwitchAndGauge/DashDrawer/Sections'
import { Button, Paragraph, FlexRow, FlexCol } from 'components/patterns'
import { TextArea } from 'components/deprecated/Inputs'
import { StyledViewWrap } from './style'
import {
  getComponentName,
  getComponentDescription,
} from 'components/SwitchAndGauge/utils'

const CustomGraphicListItem = ({ partNumber, description }) => (
  <li>
    {partNumber}: {description}
  </li>
)

CustomGraphicListItem.propTypes = {
  partNumber: PropTypes.string,
  description: PropTypes.string,
}

const ListItem = ({ panelNumber, panelDescription }) => (
  <li>
    ({panelNumber}) {panelDescription}
  </li>
)

ListItem.propTypes = {
  panelNumber: PropTypes.string,
  panelDescription: PropTypes.string,
}

const makeCustomGraphicsList = (data) =>
  data.map((item) => (
    <CustomGraphicListItem
      key={item.partNumber}
      partNumber={item.partNumber}
      description={item.description}
    />
  ))

const makeList = (components) =>
  components.map((component) => (
    <ListItem
      key={prop('panelDescription', component)}
      panelNumber={getComponentName(component)}
      panelDescription={getComponentDescription(component)}
    />
  ))

const SaveView = () => {
  const dispatch = useDispatch()

  const [notes, setNotes] = useState('')
  const updatedLocationNames = useSelector(getUpdatedLocationNames)
  const addedComponents = useSelector(getAddedComponentsList)
  const movedComponents = useSelector(getMovedComponentsList)
  const removedComponents = useSelector(getRemovedComponentsList)
  const addedSwitchGraphics = useSelector(getSelectedCustomGraphics)
  const removedSwitchGraphics = useSelector(getRemovedCustomGraphics)
  const isAllium = useSelector(getIsAllium)
  const movedList = useMemo(() => makeList(movedComponents), [movedComponents])
  const removedList = useMemo(
    () => makeList(removedComponents),
    [removedComponents]
  )
  const addedList = useMemo(() => makeList(addedComponents), [addedComponents])
  const addCustomGraphicsList = useMemo(
    () => makeCustomGraphicsList(addedSwitchGraphics),
    [addedSwitchGraphics]
  )
  const removedCustomGraphicsList = useMemo(
    () => makeCustomGraphicsList(removedSwitchGraphics),
    [removedSwitchGraphics]
  )

  const handleOnChange = (e) => {
    setNotes(e.currentTarget.value)
  }

  const handleOnSave = () => {
    dispatch(saveDash(notes))
    dispatch(setSelectedIdWithDrawer(null))
  }

  const handleOnCancel = () => {
    dispatch(
      setSelectedIdWithDrawerAndMeta(DASH_VIEW, {
        view: LIST,
      })
    )
    setNotes('')
  }

  const disableSave = notes.length === 0

  const movedComponentsSection = useMemo(
    () =>
      !isEmpty(movedList) ? (
        <>
          <StyledParagraphTwo size="small" weight="bold">
            {strings.moved_the_following}:
          </StyledParagraphTwo>
          <StyledUl>{movedList}</StyledUl>
        </>
      ) : null,
    [movedList]
  )

  const removedComponentsSection = useMemo(
    () =>
      !isEmpty(removedList) ? (
        <>
          <StyledParagraphTwo size="small" weight="bold">
            {strings.removed_the_following}:
          </StyledParagraphTwo>
          <StyledUl>{removedList}</StyledUl>
        </>
      ) : null,
    [removedList]
  )

  const addedComponentsSection = useMemo(
    () =>
      !isEmpty(addedList) ? (
        <>
          <StyledParagraphTwo size="small" weight="bold">
            {strings.added_the_following}:
          </StyledParagraphTwo>
          <StyledUl>{addedList}</StyledUl>
        </>
      ) : null,
    [addedList]
  )

  const addedCustomGraphicsSection = useMemo(
    () =>
      !isEmpty(addCustomGraphicsList) ? (
        <>
          <StyledParagraphTwo size="small" weight="bold">
            {strings.added_the_following_cap_graphic}:
          </StyledParagraphTwo>
          <StyledUl>{addCustomGraphicsList}</StyledUl>
        </>
      ) : null,
    [addCustomGraphicsList]
  )

  const removedCustomGraphicsSection = useMemo(
    () =>
      !isEmpty(removedCustomGraphicsList) ? (
        <>
          <StyledParagraphTwo size="small" weight="bold">
            {strings.removing_the_following_cap_graphics}:
          </StyledParagraphTwo>
          <StyledUl>{removedCustomGraphicsList}</StyledUl>
        </>
      ) : null,
    [removedCustomGraphicsList]
  )

  const updatedLocationsSection = useMemo(
    () =>
      !isEmpty(updatedLocationNames) ? (
        <>
          <StyledParagraphTwo size="small" weight="bold">
            {strings.save_layout_location_header}:
          </StyledParagraphTwo>
          <StyledUl>
            {updatedLocationNames.map((loc) => (
              <li key={loc}>{loc}</li>
            ))}
          </StyledUl>
        </>
      ) : null,
    [updatedLocationNames]
  )

  const hasUpdatedLocations = !isEmpty(updatedLocationNames)
  const saveBtnText = hasUpdatedLocations
    ? strings.save_layout_location_confirm
    : strings.save
  const cancelBtnText = hasUpdatedLocations
    ? strings.save_layout_location_cancel
    : strings.cancel

  return (
    <StyledViewWrap>
      <Content
        style={{
          marginTop: 15,
          height: 'auto',
          overflowY: 'hidden',
          flex: 'none',
          paddingBottom: 12,
        }}
      >
        <StyledSaveRow>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={20}
            height={18}
            fill="none"
            style={{ marginRight: 8, position: 'relative', top: 3 }}
          >
            <path
              fill="#EFB115"
              fillRule="evenodd"
              d="M19.71 14.82 11.84 1.06a2.09 2.09 0 0 0-3.63 0L.31 14.82A2.12 2.12 0 0 0 2.1 18h15.8a2.12 2.12 0 0 0 1.81-3.18zM11 14.5H9v-2h2v2zm0-5a1 1 0 0 1-2 0v-2a1 1 0 0 1 2 0v2z"
              clipRule="evenodd"
            />
            <path
              fill="#07070F"
              d="M11 12.5H9v2h2v-2zm0-3v-2a1 1 0 0 0-2 0v2a1 1 0 0 0 2 0z"
            />
          </svg>
          <StyledSaveColumn>
            <StyledParagraphOne size="large" weight="bold">
              {strings.editing_switch_and_gauge}
            </StyledParagraphOne>
          </StyledSaveColumn>
        </StyledSaveRow>
      </Content>
      <ListContent>
        <StyledSaveRow>
          <StyledSaveColumn>
            {hasUpdatedLocations && (
              <div style={{ marginBottom: 16 }}>
                <Paragraph>{strings.save_layout_location_text}</Paragraph>
              </div>
            )}
            {updatedLocationsSection}
            {addedComponentsSection}
            {movedComponentsSection}
            {removedComponentsSection}
            {addedCustomGraphicsSection}
            {removedCustomGraphicsSection}
          </StyledSaveColumn>
        </StyledSaveRow>
      </ListContent>
      <Footer
        style={{
          borderTop: 'none',
          marginBottom: -20,
        }}
      >
        <TextInputContainer>
          <StyledParagraphThree size="small">
            {strings.notes_required_star}
          </StyledParagraphThree>
          {isAllium ? (
            <StyledParagraphThree size="small">
              {strings.allium_save_warning}
            </StyledParagraphThree>
          ) : null}
          <StyledTextArea
            size="large"
            onChange={handleOnChange}
            value={notes}
            placeholder={strings.notes_required}
            autosize={{ minRows: 8, maxRows: 8 }}
            maxLength={1000}
          />
        </TextInputContainer>
        <ButtonContainer>
          <FlexRow>
            <FlexCol span={12}>
              <div>
                <Button type="secondary" onClick={handleOnCancel}>
                  {cancelBtnText}
                </Button>
              </div>
            </FlexCol>
            <FlexCol span={12} alignItems="end">
              <div>
                <Button onClick={handleOnSave} disabled={disableSave}>
                  {saveBtnText}
                </Button>
              </div>
            </FlexCol>
          </FlexRow>
        </ButtonContainer>
      </Footer>
    </StyledViewWrap>
  )
}

export default SaveView

const ButtonContainer = styled.div`
  border-top: 1px solid ${themeColor('sAndGDrawerDivider')};
`

const ListContent = styled(Content)`
  padding-bottom: 16px;
`

const TextInputContainer = styled.div`
  position: relative;
`

const StyledParagraphOne = styled(Paragraph)`
  font-size: 20px;
`

const StyledParagraphTwo = styled(Paragraph)`
  margin-bottom: 2.5%;
`

const StyledUl = styled.ul`
  margin-bottom: 3%;
  margin-left: -7%;
  max-width: 380px;

  li {
    margin-left: 10px;
  }
`

const StyledParagraphThree = styled(Paragraph)`
  margin-bottom: 3%;
`

const StyledTextArea = styled(TextArea)`
  margin-bottom: 6%;
  width: 355px !important;

  .input-footer.input-footer {
    text-align: right;
  }
`

const StyledSaveRow = styled.div`
  margin-left: 3%;
  display: flex;
  flex-direction: row;
`

const StyledSaveColumn = styled.div`
  order: 2;
  display: flex;
  flex-direction: column;
`
